import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CoreApiClient } from '@/services/coreApiClient';
import { assistantTone, groupAssistantItems, actionableAssistantItems, type AssistantItem } from './presentation';
import './AssistantInbox.css';
import { verifiedManagementEvidence, type ManagementEvidence } from '@/components/projects/ProjectManagementEvidence';

const api = new CoreApiClient('', (...args) => fetch(...args));
type Summary = { count: number; color: string; label: string; projectCount?: number };

// Mounted with the current identity as its React key; late responses are ignored on unmount.
export default function AssistantNotificationSummary() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<Summary | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const response = await api.request<{ data: { contractVersion: string; items: AssistantItem[]; lastFailure?: string; managementEvidenceProjects?: ManagementEvidence[] } }>('/api/v1/mcp/project-assistant/inbox', { method: 'GET' });
        if (!active) return;
        const data = response.data;
        if (data?.contractVersion !== 'project-assistant-v1' || !Array.isArray(data.items)) throw new Error('Unverified inbox');
        const pending = actionableAssistantItems(data.items).filter(i => ['suggested', 'unknown', 'executing', 'failed', 'conflict'].includes(i.state));
        const tone = data.lastFailure ? { color: 'red', label: 'Mail check incomplete' } : assistantTone(pending);
        const projectCount = new Set((data.managementEvidenceProjects || []).filter(value => verifiedManagementEvidence(value) && value.state === 'available'
          && value.attention.some(attention => attention.severity === 'warning')).map(value => value.projectId)).size;
        setSummary(pending.length || data.lastFailure || projectCount ? { count: groupAssistantItems(pending).length, ...tone,
          ...(projectCount ? { projectCount, ...(!data.lastFailure ? { color: 'amber', label: 'Project changes to review' } : {}) } : {}) } : null);
        setUnavailable(false);
      } catch (error) {
        if (!active) return;
        setSummary(null);
        setUnavailable(![401, 403, 404].includes(Number((error as { status?: number }).status)));
      }
    }
    void load();
    const interval = window.setInterval(() => void load(), 60000);
    const visible = () => { if (document.visibilityState === 'visible') void load(); };
    document.addEventListener('visibilitychange', visible);
    return () => { active = false; window.clearInterval(interval); document.removeEventListener('visibilitychange', visible); };
  }, []);
  if (!summary && !unavailable) return null;
  return <button type="button" className={`assistant-summary assistant-tone--${summary?.color || 'red'}`}
    onClick={() => navigate('/ai?tab=pm-agent')}>
    <strong>Project Assistant · {summary?.label || 'Unavailable'}</strong>
    <span>{summary ? (summary.count ? `${summary.count} ${summary.count === 1 ? 'follow-up' : 'follow-ups'} to review` : summary.projectCount ? 'Open inbox to review project changes' : 'Open inbox to check monitoring status') : 'Could not refresh suggestions. Open inbox to retry.'}</span>
    {summary?.projectCount ? <span>{summary.projectCount} {summary.projectCount === 1 ? 'project has' : 'projects have'} central evidence to review</span> : null}
    <small>{summary?.projectCount ? 'Open related project evidence · Saved project status stays unchanged' : 'Grouped by email · Tasks require acceptance or an enabled rule'}</small>
  </button>;
}
